import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_FINANCIAL_INDICATORS_QUERY } from './queries';
import { FINANCIAL_INDICATOR_DELETED } from './subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import ExportAction from './actions/ExportAction';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { useMutation, useSubscription } from 'hooks/apollo';
import { DELETE_FINANCIAL_INDICATOR } from './mutations';
import FinancialIndicatorItem from './FinancialIndicatorItem';

export const financialIndicatorsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'name',
      caption: 'FinancialIndicators.Name',
      type: 'text'
    },
    {
      name: 'code',
      caption: 'FinancialIndicators.Code',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'FinancialIndicators.Name',
      value: 'Name'
    },
    {
      caption: 'FinancialIndicators.Code',
      value: 'Code'
    }
  ],
  pager: {
    orderBy: 'Name',
    direction: 'asc'
  }
});

const FinancialIndicatorListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(financialIndicatorsStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_FINANCIAL_INDICATORS_QUERY,
        name: 'pagedFinancialIndicators'
      }
    }),
    []
  );

  useSubscription(FINANCIAL_INDICATOR_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialIndicators.FinancialIndicatorDeleted'), 'success');
      client.cache.evict({ id: `FinancialIndicatorItem:${data.financialIndicatorDeleted.financialIndicatorId}` });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedFinancialIndicators' });
      client.cache.gc();
    }
  });

  const [deleteFinancialIndicator] = useMutation(DELETE_FINANCIAL_INDICATOR);

  const handleDelete = useCallback(
    id => {
      confirm(
        () => {
          deleteFinancialIndicator({ variables: { input: { id } } });
        },
        {
          content: t('FinancialIndicators.DeleteFinancialIndicator')
        }
      );
    },
    [deleteFinancialIndicator, confirm, t]
  );

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedFinancialIndicators' });
      client.cache.gc();
    }
  }, [activeService, client.cache]);

  // TODO Wrong way to add actions to list-browser!! Will need a refactor
  useEffect(() => {
    addActions([
      {
        name: 'export',
        icon: <ExportAction />
      }
    ]);
  }, [addActions, t]);

  const itemComponent = useCallback(({ data }) => <FinancialIndicatorItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'orderNo', caption: 'FinancialIndicators.OrderNo', width: '100px' },
      { name: 'code', caption: 'FinancialIndicators.Code' },
      { name: 'name', caption: 'FinancialIndicators.Name' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser
        name='financial-indicators-browser'
        store={financialIndicatorsStore}
        ItemComponent={itemComponent}
        columns={columns}
      />
      <ApolloFetchingComponent store={financialIndicatorsStore} options={fetchingOptions} />
    </Card>
  );
};

export default FinancialIndicatorListBrowser;
